import React, { useState, useRef } from "react";
import { CloudUpload, Download, FileText, CheckCircle } from "lucide-react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { ReactComponent as Eye } from "../../../../../icons/View_eye.svg";
import { ReactComponent as Delete } from "../../../../../icons/View_dlt.svg";
import ModHeader from "../../Mod/ModTable/ModHeader";
import { useSelector } from "react-redux";
import { ModBulkUploadFile } from "../../../../../Rest_api/RestApi";
import Tooltip from "../../../../../components/tooltip";
import { useMeritEngineNavigation } from "../../useMeritEngineNavigation";
import MainHeader from "../../DateSession";
import { ErrorPopup } from "../../../../../components/ErrorHandling";
import { getAccessToken } from "../../../../Protected";
import { BASE_URL } from "../../../../../configs";

// Helper function to format the date as DD-MMM-YYYY
const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  const options: Intl.DateTimeFormatOptions = {
    day: "2-digit",
    month: "short",
    year: "numeric",
  };
  const formattedDate = new Intl.DateTimeFormat("en-GB", options).format(date);
  return formattedDate.replace(/ /g, "-");
};

type UploadedFile = {
  type: string;
  date: string;
  file_url: string;
  file: File;
  id: number;
  isTemplate: boolean;
};

const BaseFileRegexPatterns: { [key: string]: RegExp } = {
  AVT: /^AVT \d{2}\.\d{2}\.\d{4}\s*\.xlsx(?: - .*\.csv)?$/i,
  "Day Ahead Schedule for MTOA":
    /^Day Ahead Schedule for MTOA\((Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)-\d{2}\)( \(\d+\))?\.xlsx$/,
  "Declared Capacity of GKEL":
    /^Declared Capacity of GKEL for\s+\d{2}\.\d{2}\.\d{4}\s*\.xlsx$/,
  "IEX DOR GRIDCO": /^IEX\d{6}DOR_GDC0001_OR0_GRIDCO\.xls$/,
  "JSW Energy Utkal Schedule":
    /^JSW Energy Utkal Schedule & GRIDCO allocation for \d{2}\.\d{2}\.\d{2}\.xlsx$/,
  "NetSchdReportBreakup ISGS":
    /^NetSchdReportBreakup@ODISHA_STATE@ISGS@rev\(\d+\)@\d{2}-\d{2}-\d{4}@\d{2}-\d{2}-\d{4}_\d{2}-\d{2}-\d{2}\.xlsx$/,
  "NetSchdReportBreakup OA_GNA":
    /^NetSchdReportBreakup@ODISHA_STATE@OA_GNA@rev\(\d+\)@\d{2}-\d{2}-\d{4}@\d{2}-\d{2}-\d{4}_\d{2}-\d{2}-\d{2}\.xlsx$/,
  "NetSchdReportBreakup OA_REMC":
    /^NetSchdReportBreakup@ODISHA_STATE@OA_REMC@rev\(\d+\)@\d{2}-\d{2}-\d{4}@\d{2}-\d{2}-\d{4}_\d{2}-\d{2}-\d{2}\.xlsx$/,
  "OPGC STAGE-2 DC": /^OPGC STAGE-2 DC\s+\d{2}\.\d{2}\.\d{4}\s+-\s+R\d+\.xlsx$/,
  "RTM_IEX DOR GRIDCO": /^RTM_IEX\d{6}DOR_GDC0001_OR0_GRIDCO(\s?\(1\))?\.XLS$/,
  "SLDC LDS":
    /^SLDC LDS \d{2} (Jan(?:uary)?|Feb(?:ruary)?|Mar(?:ch)?|Apr(?:il)?|May|Jun(?:e)?|Jul(?:y)?|Aug(?:ust)?|Sep(?:tember)?|Oct(?:ober)?|Nov(?:ember)?|Dec(?:ember)?) \d{4} \(Rev-\d+\)\.xlsx$/i,
  "NBVL(IPP) DAY AHEAD SCHEDULE":
    /^NBVLIPP_DAY_AHEAD_SCHEDULE_TO_GRIDCO_FOR_(\d{2}\.\d{2}\.\d{4})\.xlsx$/,
  jipl_dc_pdf: /^JIPL DC for DD \d{2}\.\d{2}\.\d{4}\.pdf$/,
  sldc_report: /^SLDC_Report__(\d{2})(\d{2})(\d{4})\.xlsx(?: - .*\.csv)?$/i,
  Intra_State_RE: /^Intra_State_RE\.xlsx$/,
  CGP_Injection: /^CGP_Injection\.xlsx$/,
  Buffer: /^Buffer\.xlsx$/,
  Vc: /^VC_all_generators\.xlsx$/,
  Banking: /^Banking\.xlsx$/,
  "Short-Term": /^Short-Term\.xlsx$/,
};

const FileRegexPatterns: { [key: string]: RegExp } = {
  ...BaseFileRegexPatterns,
  JIPL_TEMPLATE: /^JIPL\.xlsx$/,
  JSWEUL_TEMPLATE: /^JSWEUL\.xlsx$/,
  "Maa Durga_TEMPLATE": /^Maa Durga\.xlsx$/,
  NBVL_TEMPLATE: /^NBVL\.xlsx$/,
  "NET SCHEDULED_TEMPLATE": /^NET SCHEDULED\.xlsx$/,
  OA_GNA_TEMPLATE: /^OA_GNA\.xlsx$/,
  OA_REMC_TEMPLATE: /^OA_REMC\.xlsx$/,
  "OPGC-II Unit-3&4_TEMPLATE": /^OPGC-II Unit-3&4\.xlsx$/,
  "STATE HYDRO_TEMPLATE": /^STATE HYDRO\.xlsx$/,
  Vedanta_TEMPLATE: /^Vedanta\.xlsx$/,
  GMR_TEMPLATE: /^GMR\.xlsx$/,
  IBTPS_TEMPLATE: /^IBTPS\.xlsx$/,
};

const RegexToFileTypeMap: { [key: string]: string } = {
  AVT: "OPGC-1 (IBTPS Unit -1 & 2)",
  "OPGC STAGE-2 DC": "OPGC-2 (IBTPS Unit -3 & 4)",
  "SLDC LDS": "Vedanta",
  "Declared Capacity of GKEL": "GMR",
  "JSW Energy Utkal Schedule": "JSWEUL",
  "NBVL(IPP) DAY AHEAD SCHEDULE": "NBVL",
  "NetSchdReportBreakup ISGS": "Net Schedule ISGS",
  "NetSchdReportBreakup OA_GNA": "Net Schedule OA GNA",
  "NetSchdReportBreakup OA_REMC": "Net Schedule OA REMC",
  "RTM_IEX DOR GRIDCO": "RTM day current",
  "IEX DOR GRIDCO": "DAM day current",
  "Day Ahead Schedule for MTOA": "Maa Durga",
  jipl_dc_pdf: "JIPL",
  sldc_report: "State Hydro Generation",
  Intra_State_RE: "Intra State RE",
  CGP_Injection: "CGP Injection",
  Buffer: "Buffer",
  Vc: "VC for Generators (Optional)",
  Banking: "Banking",
  "Short-Term": "Short Term Buy/Sell",
  JIPL_TEMPLATE: "JIPL Template",
  JSWEUL_TEMPLATE: "JSWEUL Template",
  "Maa Durga_TEMPLATE": "Maa Durga Template",
  NBVL_TEMPLATE: "NBVL Template",
  "NET SCHEDULED_TEMPLATE": "Net Schedule ISGS Template",
  OA_GNA_TEMPLATE: "Net Schedule OA GNA Template",
  OA_REMC_TEMPLATE: "Net Schedule OA REMC Template",
  "OPGC-II Unit-3&4_TEMPLATE": "OPGC-2 (IBTPS Unit -3 & 4) Template",
  "STATE HYDRO_TEMPLATE": "State Hydro Generation Template",
  Vedanta_TEMPLATE: "Vedanta Template",
  GMR_TEMPLATE: "GMR Template",
  IBTPS_TEMPLATE: "OPGC-1 (IBTPS Unit -1 & 2) Template",
};

const TemplateToStandardMap: { [key: string]: string } = {
  "JIPL Template": "JIPL",
  "JSWEUL Template": "JSWEUL",
  "Maa Durga Template": "Maa Durga",
  "NBVL Template": "NBVL",
  "Net Schedule ISGS Template": "Net Schedule ISGS",
  "Net Schedule OA GNA Template": "Net Schedule OA GNA",
  "Net Schedule OA REMC Template": "Net Schedule OA REMC",
  "OPGC-2 (IBTPS Unit -3 & 4) Template": "OPGC-2 (IBTPS Unit -3 & 4)",
  "State Hydro Generation Template": "State Hydro Generation",
  "Vedanta Template": "Vedanta",
  "GMR Template": "GMR",
  "OPGC-1 (IBTPS Unit -1 & 2) Template": "OPGC-1 (IBTPS Unit -1 & 2)",
};

type FileRowProps = {
  fileName: string;
  uploadedFile: File | null;
  fileError: string | null;
  isTemplate: boolean;
  onFileChange: (
    fileName: string,
    file: File | null,
    isTemplate: boolean
  ) => void;
  onFileError: (fileName: string, error: string | null) => void;
};

const getValidPatternsForFileType = (fileName: string): RegExp[] => {
  const patterns: RegExp[] = [];

  Object.entries(RegexToFileTypeMap).forEach(([regexKey, mappedType]) => {
    if (mappedType === fileName) {
      const pattern = FileRegexPatterns[regexKey];
      if (pattern) {
        patterns.push(pattern);
      }
    }
  });

  const templateKey = Object.keys(TemplateToStandardMap).find(
    (key) => TemplateToStandardMap[key] === fileName
  );
  if (templateKey) {
    const templateRegexKey = Object.keys(RegexToFileTypeMap).find(
      (key) => RegexToFileTypeMap[key] === templateKey
    );
    if (templateRegexKey && FileRegexPatterns[templateRegexKey]) {
      patterns.push(FileRegexPatterns[templateRegexKey]);
    }
  }

  return patterns;
};

const FileRow: React.FC<FileRowProps> = ({
  fileName,
  uploadedFile,
  fileError,
  isTemplate,
  onFileChange,
  onFileError,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const fileExtension = file.name.split(".").pop()?.toLowerCase();
      if (
        fileExtension !== "xlsx" &&
        fileExtension !== "csv" &&
        fileExtension !== "pdf"
      ) {
        const errorMsg =
          "Invalid file type. Only .xlsx and .csv files are allowed.";
        setError(`Error uploading "${fileName}":\n${errorMsg}`);
        onFileError(fileName, errorMsg);
        onFileChange(fileName, null, false);
        return;
      }

      const validPatterns = getValidPatternsForFileType(fileName);
      let detectedIsTemplate = false;

      if (validPatterns.length > 0) {
        const isValidFileName = validPatterns.some((pattern) =>
          pattern.test(file.name)
        );

        if (!isValidFileName) {
          const errorMsg = `File name does not match required pattern for ${fileName}.`;
          setError(`Error uploading "${fileName}":\n${errorMsg}`);
          onFileError(fileName, errorMsg);
          onFileChange(fileName, null, false);
          return;
        }

        const templateKey = Object.keys(TemplateToStandardMap).find(
          (key) => TemplateToStandardMap[key] === fileName
        );
        if (templateKey) {
          const templateRegexKey = Object.keys(RegexToFileTypeMap).find(
            (key) => RegexToFileTypeMap[key] === templateKey
          );
          if (
            templateRegexKey &&
            FileRegexPatterns[templateRegexKey] &&
            FileRegexPatterns[templateRegexKey].test(file.name)
          ) {
            detectedIsTemplate = true;
          }
        }
      }

      onFileError(fileName, null);
      onFileChange(fileName, file, detectedIsTemplate);
    } else {
      onFileChange(fileName, null, false);
      onFileError(fileName, null);
    }
  };

  const handleViewFile = () => {
    if (uploadedFile) {
      const url = URL.createObjectURL(uploadedFile);
      window.open(url, "_blank");
    }
  };

  const handleDeleteFile = () => {
    onFileChange(fileName, null, false);
    onFileError(fileName, null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const isReady = uploadedFile && !fileError;
  const isError = !uploadedFile && fileError;

  const fileConfig: Record<string, string[]> = {
    "VC for Generators (Optional)": [],
    "OPGC-2 (IBTPS Unit -3 & 4)": ["OPGC-II Unit-3&4"],
    "OPGC-1 (IBTPS Unit -1 & 2)": ["IBTPS"],
    Vedanta: ["Vedanta"],
    GMR: ["GMR"],
    JIPL: ["JIPL"],
    JSWEUL: ["JSWEUL"],
    NBVL: ["NBVL"],
    "Maa Durga": ["Maa Durga"],
    "Net Schedule OA REMC": [
      "AP41PL_BHDL",
      "AP43PL_BKN",
      "AWEK1L",
      "AWEK1L",
      "DADRI_SOLAR",
      "GADAG_GreenInfra_W",
      "GIWEL_SECI_II_RE",
      "GSECL_SOLAR",
      "OKWPL_RE",
      "RWE_AP2_SECI_III",
      "Tuticorin_BETAMWIND",
      "GPCL_SOLAR_10",
      "IRON_TRINGLE_10_MW_SOLAR",
      "XXPPL_FTG3",
      "ENERSON_SOLAR_10MW",
    ],
    "Net Schedule OA GNA": ["FARIDABAD_SOLAR", "RAJ_SOLAR", "RAJ_SOLAR"],
    "State Hydro Generation": [
      "Burla",
      "Chiplima",
      "Balimela",
      "Rengali",
      "U Kolab",
      "U Indravati",
      "Machkund O/D",
    ],
    "Net Schedule ISGS": [
      "BARH|TO_ODISHA",
      "BARH_I|TO_ODISHA",
      "CHUKHA|TO_ODISHA",
      "Darlipali_NTPC|TO_ODISHA",
      "FSTPP_III|TO_ODISHA",
      "FSTPP_III|TO_ODISHA_Solar_Beneficiary",
      "FSTPP_I_II|TO_ODISHA",
      "FSTPP_I_II|TO_ODISHA_Solar_Beneficiary",
      "KGSU1AND2|TO_ODISHA",
      "KGSU3AND4|TO_ODISHA",
      "KHSTPP_I|TO_ODISHA",
      "KHSTPP_I|TO_ODISHA_Solar_Beneficiary",
      "KHSTPP_II|TO_ODISHA",
      "KHSTPP_II|TO_ODISHA_Solar_Beneficiary",
      "KKNP|TO_ODISHA",
      "KKNPPU2|TO_ODISHA",
      "KUDGI|TO_ODISHA",
      "KURICHU|TO_ODISHA",
      "MANGDECHU|TO_ODISHA",
      "MAPS|TO_ODISHA",
      "MTPS_II|TO_ODISHA",
      "NLCEXP|TO_ODISHA",
      "NLCIIST1|TO_ODISHA",
      "NLCIIST2|TO_ODISHA",
      "NLCTS2EXP|TO_ODISHA",
      "NNTPP|TO_ODISHA",
      "NPGC|TO_ODISHA",
      "NTPL|TO_ODISHA",
      "North_Karanpura_STPS|TO_ODISHA",
      "RANGIT|TO_ODISHA",
      "RSTPSU1TO6|TO_ODISHA",
      "RSTPSU7|TO_ODISHA",
      "SIMHST1|TO_ODISHA",
      "SIMHST2|TO_ODISHA",
      "TALA|TO_ODISHA",
      "TALST2|TO_ODISHA",
      "TSTPP_I|TO_ODISHA",
      "TSTPP_I|TO_ODISHA_Solar_Beneficiary",
      "Teesta_V|TO_ODISHA",
      "TelanganaSTPP|TO_ODISHA",
      "VALLURNTECL|TO_ODISHA",
    ],
    "Intra State RE": ["SOLAR", "WIND", "BIOMASS", "SHEP"],
    "CGP Injection": ["CGP_INJECTION"],
    Buffer: ["buffer"],
    Banking: ["Total"],
    "Short Term Buy/Sell": ["Total"],
  };

  function getCustomFileName(fileName: string) {
    switch (fileName) {
      case "VC for Generators (Optional)":
        return `VC_all_generators.xlsx`;
      case "Net Schedule ISGS":
        return `NET SCHEDULED.xlsx`;
      case "OPGC-2 (IBTPS Unit -3 & 4)":
        return `OPGC-II Unit-3&4.xlsx`;
      case "OPGC-1 (IBTPS Unit -1 & 2)":
        return `IBTPS.xlsx`;
      case "Net Schedule OA GNA":
        return `OA_GNA.xlsx`;
      case "Net Schedule OA REMC":
        return `OA_REMC.xlsx`;
      case "GMR":
        return `GMR.xlsx`;
      case "JIPL":
        return `JIPL.xlsx`;
      case "JSWEUL":
        return `JSWEUL.xlsx`;
      case "NBVL":
        return `NBVL.xlsx`;
      case "Maa Durga":
        return `Maa Durga.xlsx`;
      case "State Hydro Generation":
        return `STATE HYDRO.xlsx`;
      case "Intra State RE":
        return `Intra_State_RE.xlsx`;
      case "CGP Injection":
        return `CGP_Injection.xlsx`;
      case "Buffer":
        return `Buffer.xlsx`;
      case "Short Term Buy/Sell":
        return `Short-Term.xlsx`;
      default:
        return `${fileName}.xlsx`;
    }
  }
  const preSelectedDate = useSelector(
    (state: any) => state.modGridcoDate.selectedDate
  );
  const modSelectedSession = useSelector(
    (state: any) => state.modGridcoSession.modSession
  );
  const handleDownloadTemplate = async (fileName: string) => {
    if (!fileName) return;

    const columns = fileConfig[fileName];
    if (!columns) {
      console.error("❌ Unknown fileName:", fileName);
      return;
    }

    const timeSlots = Array.from({ length: 96 }, (_, i) => i + 1);

    if (fileName === "Net Schedule ISGS") {
      const plants = columns.map((col) => {
        const [plant, sub] = col.split("|");
        return { name: plant, sub };
      });
      const headerRow = ["time_block", ...plants.map((p) => p.name)];
      const subHeaderRow = ["", ...plants.map((p) => p.sub)];
      const dataRows = timeSlots.map((slot) => [slot, ...plants.map(() => "")]);

      const worksheetData = [headerRow, subHeaderRow, ...dataRows];
      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Schedule");

      const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });
      const blob = new Blob([excelBuffer], {
        type: "application/octet-stream",
      });
      saveAs(blob, getCustomFileName(fileName));
      return;
    } else if (fileName === "VC for Generators (Optional)") {
      try {
        setError(null);
        // setLoading(true);

        const url = `${BASE_URL}/mod_gridco/getGeneratorVC?date=${preSelectedDate}&session=${modSelectedSession}`;

        const response = await fetch(url, {
          method: "GET",
          headers: {
            Authorization: `${getAccessToken() || ""}`,
          },
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(
            `Failed to fetch VC data: ${response.status} - ${errorText}`
          );
        }

        // Parse JSON response
        const vcData = await response.json();
        console.log("✅ VC data received:", vcData);

        // Check if data is an array
        if (!Array.isArray(vcData) || vcData.length === 0) {
          throw new Error("No VC data available");
        }

        // Create Excel with generator and VC columns
        // Header row
        const headerRow = ["Generator Name", "VC (P/U)"];

        // Data rows - map the JSON data
        const dataRows = vcData.map((item) => [
          item.generator || "",
          item.vc || "",
        ]);

        // Combine header and data
        const worksheetData = [headerRow, ...dataRows];

        // Create worksheet from array of arrays
        const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

        // Auto-size columns (optional)
        const columnWidths = [
          { wch: 30 }, // Generator column
          { wch: 15 }, // VC column
        ];
        worksheet["!cols"] = columnWidths;

        // Create workbook
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "VC Data");

        // Generate Excel file
        const excelBuffer = XLSX.write(workbook, {
          bookType: "xlsx",
          type: "array",
        });

        const blob = new Blob([excelBuffer], {
          type: "application/octet-stream",
        });

        saveAs(blob, getCustomFileName(fileName));

        console.log("✅ VC Generator Excel created successfully");
      } catch (error) {
        console.error("❌ Error creating VC Generator Excel:", error);
        setError(
          error instanceof Error
            ? `Failed to create VC Excel: ${error.message}`
            : "Failed to create VC Generator Excel. Please try again later."
        );
      } finally {
        // setLoading(false);
      }
      return;
    }

    // Rest of your existing code for other templates
    const data = timeSlots.map((slot) => {
      const row: Record<string, any> = { time_block: slot };
      columns.forEach((col) => {
        row[col] = "";
      });
      return row;
    });

    const worksheet = XLSX.utils.json_to_sheet(data, {
      header: ["time_block", ...columns],
    });
    XLSX.utils.sheet_add_aoa(worksheet, [["time_block", ...columns]], {
      origin: "A1",
    });

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(blob, getCustomFileName(fileName));
  };

  return (
    <div className="flex items-center justify-between py-2 px-1 border mb-3 border-[#F3F5F8] last:border-b-0">
      <div className="flex items-center gap-3">
        <FileText size={20} style={{ color: "#8C8A96" }} />
        <span className={`text-sm font-medium ${fileName ==="VC for Generators (Optional)"?"text-[#F1935C]":"text-[#525062]"}`}>
          {fileName}
        </span>
      </div>

      {isReady ? (
        <div className="flex items-center gap-4">
          <span className="text-sm" style={{ color: "#525062" }}>
            {formatDate(new Date().toISOString().split("T")[0])}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={handleViewFile}
              className="text-[#F1935C] hover:opacity-80 transition-opacity p-1 rounded"
            >
              <Eye />
            </button>
            <button
              className="flex items-center justify-center gap-2 w-[80px] h-[33px] text-xs font-medium rounded-full"
              style={{ backgroundColor: "#00BF6F", color: "#fff" }}
            >
              <CheckCircle size={14} /> <span>Ready</span>
            </button>

            <button
              onClick={handleDeleteFile}
              className="text-[#6C757D] hover:text-red-500 transition-colors p-1 rounded"
            >
              <Delete className="w-8 h-8" />
            </button>
          </div>
        </div>
      ) : isError ? (
        <div className="flex items-center gap-4">
          <span className="text-sm" style={{ color: "#525062" }}>
            {formatDate(new Date().toISOString().split("T")[0])}
          </span>
          <div className="flex items-center gap-2">
            <button
              className="flex items-center justify-center gap-2 w-[80px] h-[33px] text-xs font-medium rounded-full"
              style={{ backgroundColor: "#FDE6E8", color: "#B32130" }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-x-circle"
              >
                <circle cx="12" cy="12" r="10" />
                <path d="m15 9-6 6" />
                <path d="m9 9 6 6" />
              </svg>{" "}
              <span>Error</span>
            </button>
            <button
              onClick={handleDeleteFile}
              className="text-[#6C757D] hover:text-red-500 transition-colors p-1 rounded"
            >
              <Delete className="w-8 h-8" />
            </button>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleDownloadTemplate(fileName)}
            className="flex items-center justify-center gap-2 w-[110px] h-[30px] text-xs rounded-lg border-[0.5px] border-[#F1935C] font-medium transition-colors  bg-[#FFFAF6] text-[#F1935C]"
          >
            <Download className="w-4 h-4" />
            <span>Template</span>
          </button>
          <button
            onClick={handleUploadClick}
            className="flex items-center justify-center gap-2 w-[90px] h-[30px] text-xs rounded-lg border-[0.5px] border-[#34656D] font-medium transition-colors"
            style={{ backgroundColor: "#EFFCFF", color: "#34656D" }}
          >
            <CloudUpload size={14} /> Upload
          </button>
        </div>
      )}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        className="hidden"
        accept=".xlsx,.csv,.pdf"
      />
      <ErrorPopup error={error} onClose={() => setError(null)} />
    </div>
  );
};

export default function FileUploadTabComponent() {
  const [error, setError] = useState<string | null>(null);
  const preSelectedDate = useSelector(
    (state: any) => state.modGridcoDate.selectedDate
  );
  const modSelectedSession = useSelector(
    (state: any) => state.modGridcoSession.modSession
  );
  const [loading, setLoading] = useState(false);
  const { navigateToPage } = useMeritEngineNavigation();

  const standardFileTypes = [
    "VC for Generators (Optional)",
    "Net Schedule ISGS",
    "OPGC-1 (IBTPS Unit -1 & 2)",
    "OPGC-2 (IBTPS Unit -3 & 4)",
    "Vedanta",
    "GMR",
    "JIPL",
    "JSWEUL",
    "NBVL",
    "Maa Durga",
    "Net Schedule OA REMC",
    "Net Schedule OA GNA",
    "State Hydro Generation",
    "Intra State RE",
    "CGP Injection",
    "Buffer",
    "Banking",
    "Short Term Buy/Sell",
  ];

  const FileTypeBackendAlias: { [key: string]: string } = {
    "Net Schedule ISGS": "isgs",
    "OPGC-1 (IBTPS Unit -1 & 2)": "opgc-1",
    "OPGC-2 (IBTPS Unit -3 & 4)": "opgc-2",
    Vedanta: "vedanta",
    GMR: "gmr",
    JIPL: "jipl",
    JSWEUL: "jsweul",
    NBVL: "nbvl",
    "Maa Durga": "maa-durga",
    "Net Schedule OA REMC": "oa-remc",
    "Net Schedule OA GNA": "oa-gna",
    "State Hydro Generation": "state-hydro",
    "Intra State RE": "state-re",
    "CGP Injection": "cgp",
    Buffer: "buffer",
    Banking: "banking",
    "Short Term Buy/Sell": "short-term-buy-sell",
    "VC for Generators (Optional)": "vc-generator",
    "RTM day before": "rtm-day-before",
    "RTM day current": "rtm-day-current",
    "DAM day current": "dam-day-current",
    "Net Schedule ISGS Template": "isgs-temp",
    "OPGC-1 (IBTPS Unit -1 & 2) Template": "opgc-1-temp",
    "OPGC-2 (IBTPS Unit -3 & 4) Template": "opgc-2-temp",
    "Vedanta Template": "vedanta-temp",
    "GMR Template": "gmr-temp",
    "JIPL Template": "jipl-temp",
    "JSWEUL Template": "jsweul-temp",
    "NBVL Template": "nbvl-temp",
    "Maa Durga Template": "maa-durga-temp",
    "Net Schedule OA REMC Template": "oa-remc-temp",
    "Net Schedule OA GNA Template": "oa-gna-temp",
    "State Hydro Generation Template": "state-hydro-temp",
  };

  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [fileErrors, setFileErrors] = useState<Record<string, string | null>>(
    {}
  );

  const getStandardFileName = (fileType: string): string => {
    if (standardFileTypes.includes(fileType)) {
      return fileType;
    }
    return TemplateToStandardMap[fileType] || fileType;
  };

  const getUploadedFileForStandardType = (
    standardFileName: string
  ): { file: File | null; isTemplate: boolean } => {
    const standardFile = uploadedFiles.find(
      (f) => f.type === standardFileName && !f.isTemplate
    );
    if (standardFile) return { file: standardFile.file, isTemplate: false };

    const templateFile = uploadedFiles.find((f) => {
      const standardName = getStandardFileName(f.type);
      return standardName === standardFileName && f.isTemplate;
    });
    if (templateFile) return { file: templateFile.file, isTemplate: true };

    return { file: null, isTemplate: false };
  };

  function detectFileType(fileName: string): {
    type: string;
    isTemplate: boolean;
  } {
    for (const [regexKey, pattern] of Object.entries(FileRegexPatterns)) {
      if (pattern.test(fileName)) {
        const mappedType = RegexToFileTypeMap[regexKey];
        if (mappedType) {
          const isTemplate = mappedType.includes("Template");
          return { type: mappedType, isTemplate };
        }
      }
    }
    return { type: "", isTemplate: false };
  }

  const handleFileChange = (
    fileName: string,
    file: File | null,
    isTemplate: boolean
  ) => {
    if (!file) {
      setUploadedFiles((prevFiles) => {
        const standardFileName = getStandardFileName(fileName);

        return prevFiles.filter((f) => {
          const fStandardName = getStandardFileName(f.type);
          return fStandardName !== standardFileName;
        });
      });

      setFileErrors((prevErrors) => {
        if (!prevErrors[fileName]) {
          return {
            ...prevErrors,
            [fileName]: null,
          };
        }
        return prevErrors;
      });
      return;
    }

    const standardFileName = getStandardFileName(fileName);

    setUploadedFiles((prevFiles) => {
      const filtered = prevFiles.filter((f) => {
        const fStandardName = getStandardFileName(f.type);
        return fStandardName !== standardFileName;
      });

      return [
        ...filtered,
        {
          type: fileName,
          date: preSelectedDate,
          file_url: URL.createObjectURL(file),
          file,
          id: Date.now(),
          isTemplate: isTemplate,
        },
      ];
    });

    setFileErrors((prevErrors) => ({
      ...prevErrors,
      [fileName]: null,
    }));
  };

  const handleFileError = (fileName: string, error: string | null) => {
    setFileErrors((prevErrors) => ({
      ...prevErrors,
      [fileName]: error,
    }));
  };

  const [selectedFileType, setSelectedFileType] = useState<string>("All Files");

  const filesToDisplay =
    selectedFileType === "All Files"
      ? standardFileTypes
      : standardFileTypes.filter((fileType) => fileType === selectedFileType);

  const bulkFileInputRef = useRef<HTMLInputElement>(null);

  const handleBulkUploadClick = () => {
    bulkFileInputRef.current?.click();
  };

  function handleBulkUpload(event: React.ChangeEvent<HTMLInputElement>) {
    const files = event.target.files;
    if (!preSelectedDate) {
      setError("Please select a date");
      event.target.value = "";
      return;
    }

    if (!files || files.length === 0) {
      setError("Please select files");
      return;
    }

    const validExtensions = [".xlsx", ".xls", ".pdf"];
    const newFiles: UploadedFile[] = [];
    const undetectedFiles: string[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const fileExtension = file.name
        .substring(file.name.lastIndexOf("."))
        .toLowerCase();

      if (!validExtensions.includes(fileExtension)) {
        setError(
          `Invalid file format for ${file.name}. Please select only XLSX, XLS or PDF files.`
        );
        event.target.value = "";
        return;
      }

      const { type: detectedType, isTemplate } = detectFileType(file.name);

      if (!detectedType) {
        undetectedFiles.push(file.name);
        continue;
      }

      newFiles.push({
        type: detectedType,
        date: preSelectedDate,
        file_url: URL.createObjectURL(file),
        file,
        id: Date.now() + i,
        isTemplate: isTemplate,
      });
    }

    if (undetectedFiles.length > 0) {
      setError(
        `Could not auto-detect file type for:\n${undetectedFiles.join(
          "\n"
        )}\n\nPlease upload these files individually.`
      );
    }

    if (newFiles.length > 0) {
      setUploadedFiles((prev) => {
        const affectedStandardTypes = new Set<string>();
        newFiles.forEach((file) => {
          const standardName = getStandardFileName(file.type);
          affectedStandardTypes.add(standardName);
        });

        prev.forEach((f) => {
          const standardName = getStandardFileName(f.type);
          if (affectedStandardTypes.has(standardName)) {
            URL.revokeObjectURL(f.file_url);
          }
        });

        const kept = prev.filter((f) => {
          const standardName = getStandardFileName(f.type);
          return !affectedStandardTypes.has(standardName);
        });

        return [...kept, ...newFiles];
      });

      setFileErrors((prev) => {
        const updated = { ...prev };
        newFiles.forEach((f) => {
          const standardName = getStandardFileName(f.type);
          updated[standardName] = null;
        });
        return updated;
      });
    }

    event.target.value = "";
  }

  const GotoModTable = async () => {
    // Get all required files (excluding VC for Generators)
    const requiredFileTypes = standardFileTypes.filter(
      (type) => type !== "VC for Generators (Optional)"
    );

    // Check if VC file is uploaded
    const vcFile = getUploadedFileForStandardType(
      "VC for Generators (Optional)"
    );
    const isVcUploaded = vcFile.file !== null;

    // Get uploaded required files
    const uploadedRequiredTypes = requiredFileTypes.filter((standardType) => {
      const { file } = getUploadedFileForStandardType(standardType);
      return file !== null;
    });

    // Validation logic:
    // If VC is uploaded: all 18 files must be present (17 required + 1 VC)
    // If VC is not uploaded: all 17 required files must be present
    if (isVcUploaded) {
      // VC is uploaded, so we need all 18 files
      const totalUploaded = uploadedRequiredTypes.length + 1; // +1 for VC
      if (totalUploaded !== 18) {
        setError(
          `VC file is uploaded. Please upload all remaining files. Currently uploaded: ${totalUploaded}/18`
        );
        return;
      }
    } else {
      // VC is not uploaded, so we need all 17 required files
      if (uploadedRequiredTypes.length !== 17) {
        setError(
          `Please upload all required files. Currently uploaded: ${uploadedRequiredTypes.length}/17 (VC file is optional)`
        );
        return;
      }
    }

    try {
      setLoading(true);
      const formData = new FormData();

      const metaData = uploadedFiles.map((file, idx) => {
        const field = `file_${idx}`;
        formData.append(field, file.file);

        let backendType: string;
        if (file.isTemplate) {
          const standardName = getStandardFileName(file.type);
          const baseAlias = FileTypeBackendAlias[standardName];
          backendType = baseAlias ? `${baseAlias}-temp` : file.type;
        } else {
          backendType = FileTypeBackendAlias[file.type] || file.type;
        }

        return {
          field,
          date: file.date,
          type: backendType,
        };
      });

      formData.append("metaData", JSON.stringify(metaData));
      formData.append("date", preSelectedDate);
      formData.append("session", modSelectedSession);
      const res = await ModBulkUploadFile(formData);
      setUploadedFiles([]);
      setSelectedFileType("All Files");

      if (res) {
        navigateToPage("merit-order-dispatch");
        (window as any).resetNextTabs?.("GENERATION");
      }
    } catch (error) {
      console.error("Error generating file:", error);
      setError("Error generating file. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Count uploaded files
  const uploadedRequiredCount = standardFileTypes
    .filter((type) => type !== "VC for Generators (Optional)")
    .filter((standardType) => {
      const { file } = getUploadedFileForStandardType(standardType);
      return file !== null;
    }).length;

  const vcFile = getUploadedFileForStandardType("VC for Generators (Optional)");
  const isVcUploaded = vcFile.file !== null;

  // Button should be enabled if:
  // - VC not uploaded AND all 17 required files uploaded
  // - VC uploaded AND all 18 files uploaded
  const isButtonEnabled = isVcUploaded
    ? uploadedRequiredCount === 17 && isVcUploaded
    : uploadedRequiredCount === 17;

  return (
    <div className="flex flex-col w-full">
      <MainHeader />
      <div className="h-[calc(-73px+100vh)] p-4">
        <div className="w-full h-full flex flex-col gap-4">
          <ModHeader title="Available Generation Capacity" />
          <div className="gas-chart-container ">
            <div className="gas-heading gap-x-10">
              <div className="flex gap-x-6 w-full justify-between">
                <div className="flex gap-x-4">
                  <select
                    className="border border-[#C5C5CBD9] rounded-lg p-2 max-w-[200px] text-[#8C8A96]"
                    value={selectedFileType}
                    onChange={(e) => setSelectedFileType(e.target.value)}
                  >
                    <option value="All Files">All Files</option>
                    {standardFileTypes.map((fileType, index) => (
                      <option key={index} value={fileType}>
                        {fileType}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex gap-x-4">
                  <button
                    type="submit"
                    disabled={!isButtonEnabled || loading}
                    onClick={GotoModTable}
                    className="flex items-center gap-2 px-3 text-sm rounded-xl disabled:text-[#A9A8B1] disabled:bg-[#E2E2E5] bg-[#F1935C] text-white font-medium"
                  >
                    {loading ? "Processing..." : `Verify & Generate MOD`}
                  </button>
                </div>
              </div>
            </div>

            <div
              className="p-4 mt-3"
              style={{
                backgroundColor: "#F1F5F980",
                border: "1px solid #CAD5E2",
                borderRadius: "15px",
                boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.05)",
              }}
            >
              <div className="flex justify-between items-center">
                <div>
                  <h3
                    className="text-xl font-medium"
                    style={{ color: "#6F6D7C" }}
                  >
                    Add Files
                  </h3>
                  <p className="text-large" style={{ color: "#8C8A96" }}>
                    Attach all the mentioned files below, either individual
                    files or all the files together, to generate the MOD Table.
                    You can upload either standard files or template files. VC
                    for Generators is optional.
                  </p>
                </div>
                <button
                  onClick={handleBulkUploadClick}
                  className="flex items-center justify-center gap-2 w-[180px] h-[37px] text-base rounded-lg border-[0.5px] border-[#34656D] font-medium transition-colors"
                  style={{ backgroundColor: "#EFFCFF", color: "#34656D" }}
                >
                  <CloudUpload size={20} /> Bulk Upload
                </button>
              </div>
              <input
                type="file"
                ref={bulkFileInputRef}
                onChange={handleBulkUpload}
                className="hidden"
                multiple
                accept=".xlsx, .xls, .pdf"
              />
            </div>
            <h2
              className="mx-4 py-2 chartHeading border-b !text-lg !font-medium"
              style={{ color: "#6F6D7C" }}
            >
              File Types
            </h2>

            <div className="px-4 h-100 overflow-y-auto">
              {filesToDisplay.map((fileName) => {
                const { file: uploadedFile, isTemplate } =
                  getUploadedFileForStandardType(fileName);
                return (
                  <FileRow
                    key={fileName}
                    fileName={fileName}
                    uploadedFile={uploadedFile}
                    isTemplate={isTemplate}
                    fileError={fileErrors[fileName] || null}
                    onFileChange={handleFileChange}
                    onFileError={handleFileError}
                  />
                );
              })}
            </div>
          </div>
          <ErrorPopup error={error} onClose={() => setError(null)} />
          <div className="gna-footer-container">
            <div className="grid grid-cols-3 justify-between gap-5 ">
              <Tooltip
                note={
                  <p>
                    Default values and variable charges auto-fetched from
                    system.
                  </p>
                }
                text=" "
              >
                <p className="gna-footer-text">Source</p>
              </Tooltip>
              <p className="gna-footer-text col-span-1 text-center">
                Powered by <strong>GNA Energy</strong>
              </p>
              <strong className="gna-footer-text col-span-1 text-end">
                Beta Version
              </strong>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
